import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { matchdayApi } from '@/api/endpoints';
import type { MatchdayRanking } from '@/api/types';
import { Alert, Card, Notice, Spinner } from '@/components/ui';
import { useAsync } from '@/lib/useAsync';
import adminStyles from '@/features/admin/admin.module.css';

export function MatchdayRankingPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);

  const { data, loading, error } = useAsync<MatchdayRanking | { locked: true }>(
    async (signal) => {
      try {
        return await matchdayApi.ranking(matchdayId, signal);
      } catch (err) {
        // §11.4: vor Tippschluss liefert das Backend 423 LOCKED.
        if (err instanceof ApiError && err.status === 423) return { locked: true };
        throw err;
      }
    },
    [matchdayId],
    { refreshOnFocus: true },
  );

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (loading) return <Spinner label="Ranking wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Alert>Keine Daten.</Alert>;

  if ('locked' in data) {
    return (
      <>
        <p className="muted">
          <Link to={`/matchdays/${matchdayId}`}>← Zum Spieltag</Link>
        </p>
        <h1>Ranking</h1>
        <Notice>Das Ranking ist erst nach Tippschluss sichtbar.</Notice>
      </>
    );
  }

  const { matchday, ranking } = data;

  return (
    <>
      <p className="muted">
        <Link to={`/matchdays/${matchday.id}`}>← Zum Spieltag</Link>
      </p>
      <h1>Ranking — {matchday.name}</h1>

      {ranking.length === 0 ? (
        <Card>
          <p className="muted">Noch keine Wertung vorhanden.</p>
        </Card>
      ) : (
        <Card>
          <table className={adminStyles.rankTable}>
            <thead>
              <tr>
                <th className={adminStyles.rankPos}>#</th>
                <th>Teilnehmer</th>
                <th className={adminStyles.rankPoints}>Punkte</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((entry, i) => (
                <tr key={entry.user_id}>
                  <td className={adminStyles.rankPos}>{i + 1}</td>
                  <td>{entry.display_name}</td>
                  <td className={adminStyles.rankPoints}>{entry.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
